import React from 'react';
import {
  Box,
  Button,
  Code,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  IconButton,
  Portal,
  Tooltip,
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'hooks';

const STORAGE_KEY = 'instance-info-dock-expanded';

const getInitialExpandedState = () => {
  if (typeof window === 'undefined') return true;
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === null) return true;
  return v === 'true';
};

const InstanceInfoAlert = (): JSX.Element | null => {
  const { t } = useTranslation();
  const version = useAppSelector(state => state.consts.version);
  const [isExpanded, setIsExpanded] = React.useState(getInitialExpandedState);

  React.useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, String(isExpanded));
  }, [isExpanded]);

  if (!version || version.instance === 'prod' || version.instance === 'rc') {
    return null;
  }

  return (
    <>
      <Tooltip
        hasArrow
        label={
          t(
            'youAreOnANonProductionInstance',
            'Rozwiń - Jesteś na nieprodukcyjnej instacji',
          ) + ` ${version.instance}`
        }
      >
        <IconButton
          aria-label={t('expand', 'Informacje o instancji')}
          icon={<InfoIcon />}
          size='md'
          variant='ghost'
          color='blue.500'
          onClick={() => setIsExpanded(prev => !prev)}
        />
      </Tooltip>

      {isExpanded && (
        <Portal>
          <Box
            position='fixed'
            right={4}
            top={1}
            zIndex='toast'
            pointerEvents='none'
          >
            <Alert
              status='info'
              borderRadius='md'
              paddingX={6}
              paddingY={4}
              pointerEvents='auto'
            >
              <AlertIcon />
              <Box>
                <AlertTitle>
                  {t(
                    'youAreOnANonProductionInstance',
                    'Jesteś na nieprodukcyjnej instacji',
                  )}{' '}
                  <Code>{version.instance}</Code> 🛠️
                </AlertTitle>
                <AlertDescription>
                  <Flex gridGap={6} wrap='wrap'>
                    <Box>
                      api <Code>{version.api}</Code>
                    </Box>
                    <Box>
                      ui <Code>{version.ui}</Code>
                    </Box>
                  </Flex>
                </AlertDescription>
              </Box>
              <Button
                size='sm'
                variant='ghost'
                onClick={() => setIsExpanded(false)}
                flexShrink={0}
              >
                {t('collapse', 'Zwiń')}
              </Button>
            </Alert>
          </Box>
        </Portal>
      )}
    </>
  );
};

export default InstanceInfoAlert;
